import { useEffect, useMemo, useRef, useState } from 'react'
import { Conversion } from './UnitConversionsDrawer'

type Props = {
  productName: string
  conversions?: Conversion[]
  onOpenConversions: () => void
}

export default function InlineConverter({ productName, conversions = [], onOpenConversions }: Props) {
  const [open, setOpen] = useState(false)
  const [convId, setConvId] = useState<string>('')
  const [val, setVal] = useState<string>('1')
  const panelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (conversions.length > 0 && !convId) setConvId(conversions[0].id)
  }, [conversions, convId])

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (!panelRef.current) return
      if (!panelRef.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  const result = useMemo(() => {
    const n = Number(val)
    const c = conversions.find(r => r.id === convId)
    if (!c || !Number.isFinite(n)) return ''
    return (n * c.factor).toFixed(4)
  }, [val, convId, conversions])

  function copy() {
    if (!result) return
    navigator.clipboard?.writeText(result)
  }

  if (conversions.length === 0) {
    return (
      <button className="btn-secondary" onClick={onOpenConversions} title="Set unit conversions first">
        Set Conversions
      </button>
    )
  }

  return (
    <div className="relative">
      <button className="btn-secondary" onClick={() => setOpen(v => !v)} title="Inline converter">
        Convert
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute z-40 right-0 mt-2 w-[320px] rounded-xl border border-[color:var(--border)] bg-white shadow-xl p-3"
        >
          <div className="text-xs font-semibold mb-2 line-clamp-1">{productName}</div>

          <div className="grid gap-2">
            <select className="select" value={convId} onChange={e => setConvId(e.target.value)}>
              {conversions.map(c => (
                <option key={c.id} value={c.id}>
                  {c.from} → {c.to} × {c.factor}
                </option>
              ))}
            </select>

            <div className="grid grid-cols-2 gap-2">
              <input className="input" placeholder="Value" value={val} onChange={e => setVal(e.target.value)} />
              <input className="input" readOnly placeholder="Result" value={result} />
            </div>

            <div className="flex gap-2 justify-end">
              <button className="btn-secondary" onClick={copy} disabled={!result}>Copy</button>
              <button className="btn-secondary" onClick={() => setOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
